/**
 * Design estimate ("bluesky") on the infinite board (eval/TAKEOFF_PLAN.md):
 * the fast fixture library, the quick-add command grammar, fixture element
 * tagging and budget formatting. Framework-free; the board app turns these
 * into Excalidraw elements.
 *
 * A placed fixture is an ordinary board element carrying
 *   customData.estimate = { v: 1, role: 'fixture', item_id | assembly_id, placed_at, measure, qty?, length?, area? }
 * and its bound label `{ v: 1, role: 'label' }`. The server reads the scene,
 * so quantities, numbering, exports and the AI all see the same thing.
 */
import { TYPE_PREFIXES, type MeasureKind, type TakeoffCategory, type TakeoffItemDTO, type TakeoffSymbol, type Uom } from './takeoff';

export interface FixtureData {
  v: 1;
  role: 'fixture';
  item_id?: string;
  assembly_id?: string;
  /** ms epoch; numbering is (placed_at, element id) per target per frame, the same on server and client. */
  placed_at: number;
  measure: MeasureKind;
  /** Count on this one symbol ("×4"). */
  qty?: number;
  /** Typed run length / area (item UOM) — wins over the frame scale. */
  length?: number;
  area?: number;
  /** "Count along the run" (a count fixture on a line): ceil(measured length / unit_length) of an EACH type. */
  along_run?: AlongRun;
  /** Bid scope of this one placement (wins over its zone, type and frame); absent = inherit. */
  scope?: string;
}
export interface AlongRun {
  unit_length: number;
  unit: 'm' | 'ft';
}
export interface FixtureLabelData {
  v: 1;
  role: 'label';
}

export interface FixturePreset {
  key: string;
  name: string;
  group: 'Luminaires' | 'AV' | 'Interactive & special effects' | 'Controls & infrastructure';
  prefix: (typeof TYPE_PREFIXES)[number];
  category: TakeoffCategory;
  uom: Uom;
  symbol: TakeoffSymbol;
  color: string;
  description: string;
  /** Words the quick-add bar matches. */
  keywords: string[];
}

const P = (
  key: string,
  name: string,
  group: FixturePreset['group'],
  prefix: FixturePreset['prefix'],
  category: TakeoffCategory,
  uom: Uom,
  symbol: TakeoffSymbol,
  color: string,
  keywords: string[] = [],
): FixturePreset => ({ key, name, group, prefix, category, uom, symbol, color, description: name.toUpperCase(), keywords: [key, ...keywords] });

/** No prices on purpose: a ROM states its gaps (TBD) rather than inventing numbers. */
export const FIXTURE_PRESETS: readonly FixturePreset[] = [
  P('downlight', 'Downlight', 'Luminaires', 'LD', 'luminaire', 'EACH', 'circle', '#E53935', ['recessed', 'can']),
  P('linear', 'Linear luminaire', 'Luminaires', 'LX', 'luminaire', 'EACH', 'square', '#FB8C00', ['linear', 'batten']),
  P('wallwasher', 'Wall-washer', 'Luminaires', 'LX', 'luminaire', 'EACH', 'square', '#F4511E', ['washer', 'wash']),
  P('uplight', 'Uplight', 'Luminaires', 'UP', 'luminaire', 'EACH', 'triangle', '#7CB342', ['inground', 'in-ground']),
  P('theatrical', 'Theatrical fixture', 'Luminaires', 'LX', 'luminaire', 'EACH', 'hexagon', '#C2185B', ['theatrical', 'theatre', 'stage', 'ellipsoidal', 'profile', 'leko', 'source four', 'fresnel', 'par', 'moving head', 'spot']),
  P('gobo', 'Projector / gobo', 'Luminaires', 'FX', 'luminaire', 'EACH', 'star', '#8E24AA', ['projector light', 'gobo']),
  P('strip', 'Strip LED', 'Luminaires', 'L', 'luminaire', 'LF', 'square', '#FDD835', ['tape', 'strip', 'cove']),
  P('pixel', 'Pixel tape', 'Luminaires', 'L', 'luminaire', 'LF', 'square', '#00ACC1', ['pixel', 'rgb']),
  P('pendant', 'Pendant', 'Luminaires', 'LX', 'luminaire', 'EACH', 'circle', '#6D4C41', ['pendant', 'chandelier']),
  P('projector', 'Video projector', 'AV', 'VD', 'av', 'EACH', 'hexagon', '#3949AB', ['projection', 'projector']),
  P('display', 'Display', 'AV', 'VD', 'av', 'EACH', 'square', '#1E88E5', ['screen', 'monitor', 'tv', 'lcd']),
  P('ledwall', 'LED wall', 'AV', 'VD', 'av', 'SF', 'square', '#283593', ['led wall', 'video wall', 'dvled']),
  P('speaker', 'Speaker', 'AV', 'AU', 'av', 'EACH', 'circle', '#43A047', ['loudspeaker', 'speaker']),
  P('sub', 'Subwoofer', 'AV', 'AU', 'av', 'EACH', 'circle', '#2E7D32', ['sub', 'subwoofer', 'bass']),
  P('mediaserver', 'Media server', 'AV', 'CP', 'control', 'EACH', 'square', '#5E35B1', ['media', 'server', 'player']),
  // Interactive (IN, an AV prefix in the workbook) and special effects (FX)
  P('motionsensor', 'Motion / presence sensor', 'Interactive & special effects', 'IN', 'av', 'EACH', 'triangle', '#00838F', ['motion', 'presence', 'pir', 'occupancy']),
  P('lidar', 'LiDAR / tracking sensor', 'Interactive & special effects', 'IN', 'av', 'EACH', 'triangle', '#00695C', ['lidar', 'tracking', 'people counter']),
  P('trackcam', 'Tracking camera', 'Interactive & special effects', 'IN', 'av', 'EACH', 'triangle', '#26A69A', ['camera', 'vision', 'kinect', 'depth']),
  P('beambreak', 'IR beam break', 'Interactive & special effects', 'IN', 'av', 'EACH', 'triangle', '#4DB6AC', ['beam', 'infrared', 'trigger', 'photo eye']),
  P('touchkiosk', 'Touch screen / kiosk', 'Interactive & special effects', 'IN', 'av', 'EACH', 'square', '#0277BD', ['touch', 'kiosk', 'touchscreen']),
  P('interactiveprojection', 'Interactive projection', 'Interactive & special effects', 'IN', 'av', 'EACH', 'hexagon', '#283593', ['interactive floor', 'interactive wall', 'reactive']),
  P('pushbutton', 'Push button / station', 'Interactive & special effects', 'IN', 'av', 'EACH', 'circle', '#F9A825', ['button', 'arcade', 'push']),
  P('rfid', 'RFID / NFC reader', 'Interactive & special effects', 'IN', 'av', 'EACH', 'circle', '#6A1B9A', ['rfid', 'nfc', 'wristband', 'tag']),
  P('pressuremat', 'Pressure mat / floor sensor', 'Interactive & special effects', 'IN', 'av', 'SF', 'square', '#8D6E63', ['pressure', 'floor sensor', 'mat']),
  P('fog', 'Fog / haze machine', 'Interactive & special effects', 'FX', 'other', 'EACH', 'star', '#90A4AE', ['fog', 'haze', 'smoke']),
  P('mist', 'Mist / water screen', 'Interactive & special effects', 'FX', 'other', 'EACH', 'star', '#4FC3F7', ['mist', 'water screen', 'fogscreen']),
  P('waterfx', 'Water effect / jet', 'Interactive & special effects', 'FX', 'other', 'EACH', 'star', '#0288D1', ['water', 'fountain', 'jet', 'spray']),
  P('bubbles', 'Bubble machine', 'Interactive & special effects', 'FX', 'other', 'EACH', 'star', '#BA68C8', ['bubble']),
  P('wind', 'Wind / fan effect', 'Interactive & special effects', 'FX', 'other', 'EACH', 'star', '#B0BEC5', ['wind', 'fan effect', 'blower']),
  P('snow', 'Snow / foam machine', 'Interactive & special effects', 'FX', 'other', 'EACH', 'star', '#E0E0E0', ['snow', 'foam']),
  P('flame', 'Flame / fire effect', 'Interactive & special effects', 'FX', 'other', 'EACH', 'star', '#E64A19', ['flame', 'fire', 'pyro']),
  P('laser', 'Laser projector', 'Interactive & special effects', 'FX', 'other', 'EACH', 'star', '#D81B60', ['laser']),
  P('scent', 'Scent diffuser', 'Interactive & special effects', 'FX', 'other', 'EACH', 'star', '#A1887F', ['scent', 'aroma', 'smell']),
  P('strobe', 'Strobe / lightning effect', 'Interactive & special effects', 'FX', 'luminaire', 'EACH', 'star', '#FFEE58', ['strobe', 'lightning', 'flash']),
  P('showcontrol', 'Show controller', 'Controls & infrastructure', 'CP', 'control', 'EACH', 'diamond', '#8E24AA', ['show', 'controller', 'pharos']),
  P('controlcpu', 'Control CPU', 'Controls & infrastructure', 'CP', 'control', 'EACH', 'square', '#455A64', ['control cpu', 'cpu', 'control processor', 'processor', 'q-sys core']),
  P('gateway', 'Network gateway', 'Controls & infrastructure', 'NE', 'network', 'EACH', 'diamond', '#1565C0', ['network gateway', 'gateway', 'router', 'firewall', 'vpn']),
  P('cellmodem', 'Cellular modem', 'Controls & infrastructure', 'NE', 'network', 'EACH', 'diamond', '#6A1B9A', ['cellular modem', 'cellular', 'modem', 'lte', '4g', '5g', 'cell router']),
  P('dmxnode', 'DMX / Art-Net node', 'Controls & infrastructure', 'NE', 'network', 'EACH', 'diamond', '#00897B', ['dmx', 'node', 'artnet', 'sacn']),
  P('pixeldecoder', 'Pixel decoder', 'Controls & infrastructure', 'NE', 'control', 'EACH', 'diamond', '#00796B', ['pixel decoder', 'decoder', 'pixel controller', 'spi', 'dmx decoder', 'driver']),
  P('switch', 'Network switch', 'Controls & infrastructure', 'SW', 'network', 'EACH', 'diamond', '#546E7A', ['switch', 'poe']),
  P('rack', 'Equipment rack', 'Controls & infrastructure', 'RB', 'other', 'EACH', 'square', '#37474F', ['rack', 'cabinet']),
  P('psu', 'Power supply / driver', 'Controls & infrastructure', 'EC', 'driver', 'EACH', 'square', '#757575', ['psu', 'driver', 'power']),
];

export const PRESET_GROUPS: FixturePreset['group'][] = ['Luminaires', 'AV', 'Interactive & special effects', 'Controls & infrastructure'];

/** Is this element a fixture (not deleted)? */
export function fixtureOf(el: { customData?: unknown; isDeleted?: boolean }): FixtureData | null {
  if (el.isDeleted) return null;
  const est = (el.customData as { estimate?: { role?: string } } | undefined)?.estimate;
  return est && est.role === 'fixture' ? (est as FixtureData) : null;
}

export function isFixtureLabel(el: { customData?: unknown }): boolean {
  return (el.customData as { estimate?: { role?: string } } | undefined)?.estimate?.role === 'label';
}

export function fixtureTargetId(f: FixtureData): string | null {
  return f.item_id ?? f.assembly_id ?? null;
}

/** "LD01", "LD01 ×4", "LX02 24 ft". */
export function fixtureLabel(code: string, f: Pick<FixtureData, 'qty' | 'length' | 'area' | 'measure'>, uom?: string): string {
  if (f.measure === 'linear' && f.length) return `${code} ${trim(f.length)} ${uomShort(uom ?? 'LF')}`;
  if (f.measure === 'area' && f.area) return `${code} ${trim(f.area)} ${uomShort(uom ?? 'SF')}`;
  return f.qty && f.qty !== 1 ? `${code} ×${trim(f.qty)}` : code;
}

/**
 * Fixtures of `unitLength` needed along `length` (same unit), as the server and the workbook's
 * ROUNDUP(length/unit,0) compute it: the length is rounded to 2 decimals first, exact multiples aren't bumped.
 */
export function countAlong(length: number, unitLength: number): number {
  if (!(unitLength > 0) || !(length >= 0)) return 0;
  return Math.max(0, Math.ceil(Math.round(length * 100) / 100 / unitLength - 1e-9));
}

/** "LX01 ×20 (23.06 m ÷ 1.2 m)" — `length` is in the run's own unit. */
export function alongRunLabel(code: string, length: number, a: AlongRun): string {
  const n = countAlong(length, a.unit_length);
  return `${code} ×${n} (${trim(length)} ${a.unit} ÷ ${trim(a.unit_length)} ${a.unit})`;
}

function uomShort(uom: string): string {
  return ({ LF: 'ft', M: 'm', SF: 'sf', M2: 'm²' } as Record<string, string>)[uom] ?? uom.toLowerCase();
}

function trim(x: number): string {
  return Number.isInteger(x) ? String(x) : String(Math.round(x * 100) / 100);
}

// ---- quick add ------------------------------------------------------------------------------

export interface QuickAdd {
  /** Full TYPE code when typed with digits (LD07), else null. */
  code: string | null;
  prefix: string | null;
  qty: number | null;
  wattage: string | null;
  unitCost: number | null;
  labourHours: number | null;
  length: number | null;
  description: string;
  preset: FixturePreset | null;
}

const PREFIXES = new Set<string>(TYPE_PREFIXES);

/**
 * "LD 6 downlight 12W $180" → prefix LD, qty 6, wattage 12W, unit cost 180,
 * description DOWNLIGHT. Also: "LD07 4" (existing code, qty), "speaker 8 $450 1.5h",
 * "strip 24ft". Words matching a library preset pick its prefix / UOM / symbol.
 */
export function parseQuickAdd(input: string): QuickAdd {
  let text = ` ${input.trim()} `;
  const take = (re: RegExp): RegExpMatchArray | null => {
    const m = text.match(re);
    if (m) text = text.replace(m[0], ' ');
    return m;
  };
  const cost = take(/\s\$\s?([\d,]+(?:\.\d+)?)(?=\s)/);
  const watts = take(/\s(\d+(?:\.\d+)?)\s?w(?=\s)/i);
  const hours = take(/\s(\d+(?:\.\d+)?)\s?(?:h|hr|hrs|hours?)(?=\s)/i);
  const len = take(/\s(\d+(?:\.\d+)?)\s?(?:ft|lf|'|feet)(?=\s)/i);
  let code: string | null = null;
  let prefix: string | null = null;
  const words = text.trim().split(/\s+/).filter(Boolean);
  const rest: string[] = [];
  let qty: number | null = null;
  for (const w of words) {
    const m = w.match(/^([A-Za-z]{1,2})(\d{1,3})?$/);
    if (!prefix && m && PREFIXES.has(m[1].toUpperCase()) && (m[2] || w === w.toUpperCase())) {
      prefix = m[1].toUpperCase();
      if (m[2]) code = `${prefix}${m[2].padStart(2, '0')}`;
      continue;
    }
    if (qty === null && /^\d+(\.\d+)?$/.test(w)) {
      qty = Number(w);
      continue;
    }
    rest.push(w);
  }
  const description = rest.join(' ');
  const lower = ` ${description.toLowerCase()} `;
  // The most specific match wins ("pixel decoder" over "pixel"); whole words beat substrings.
  const best = (whole: boolean) => {
    let hit: FixturePreset | null = null;
    let len = 0;
    for (const p of FIXTURE_PRESETS)
      for (const k of p.keywords)
        if (k.length > len && lower.includes(whole ? ` ${k} ` : k)) {
          hit = p;
          len = k.length;
        }
    return hit;
  };
  const preset = best(true) ?? best(false);
  if (!prefix && preset) prefix = preset.prefix;
  return {
    code,
    prefix,
    qty,
    wattage: watts ? `${watts[1]}W` : null,
    unitCost: cost ? Number(cost[1].replace(/,/g, '')) : null,
    labourHours: hours ? Number(hours[1]) : null,
    length: len ? Number(len[1]) : null,
    description: (description || preset?.name || '').toUpperCase(),
    preset,
  };
}

/** Item fields for a quick-add or a preset ("Add to project"). */
/** Presets are written in feet; a metric project (Units: m) gets metres / square metres instead. */
export function presetUom(uom: Uom, display?: string | null): Uom {
  if (display !== 'm') return uom;
  return (uom === 'LF' ? 'M' : uom === 'SF' ? 'M2' : uom) as Uom;
}

export function itemFromPreset(p: FixturePreset, overrides: Partial<TakeoffItemDTO> = {}, display?: string | null): Record<string, unknown> {
  return {
    name: p.name,
    description: p.description,
    category: p.category,
    uom: presetUom(p.uom, display),
    symbol: p.symbol,
    color: p.color,
    source: 'design',
    ...overrides,
  };
}

// ---- money ------------------------------------------------------------------------------------

/** Intl.NumberFormat is slow to build and the panel formats every row per render: one per (currency, digits). */
const formatters = new Map<string, Intl.NumberFormat>();
export function currencyFormat(currency: string, maxDigits: number): Intl.NumberFormat {
  const key = `${currency}|${maxDigits}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat('en-CA', { style: 'currency', currency, maximumFractionDigits: maxDigits });
    formatters.set(key, f);
  }
  return f;
}

export function money(x: number | null | undefined, currency = 'CAD'): string {
  if (x === null || x === undefined || Number.isNaN(x)) return 'TBD';
  try {
    return currencyFormat(currency, x >= 1000 ? 0 : 2).format(x);
  } catch {
    return `${currency} ${x.toFixed(2)}`;
  }
}

/** Compact range: "$118k – $176k". */
export function moneyRange(low: number, high: number, currency = 'CAD'): string {
  const whole = (v: number) => {
    try {
      return currencyFormat(currency, 0).format(v);
    } catch {
      return `${currency} ${Math.round(v)}`;
    }
  };
  const k = (v: number) => (Math.abs(v) >= 10_000 ? `${whole(Math.round(v / 1000))}k` : whole(v));
  return `${k(low)} – ${k(high)}`;
}
